import * as React from "react";
import { useNavigate } from "@opendash/router";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { AppLayout } from "./AppLayout";
import { useTexts } from "../hooks/useTexts";
import {
  TabContainer,
  TabButton,
  ContentContainer,
  ChartSection,
  Card,
  InfoBox,
  FigureCaption,
  SourcesSection,
} from "./common/StyledComponents";
import { LinkifyText } from "./common/LinkifyText";
import { processInlineFormatting } from "./common/RenderText";

export const WasserentwicklungRoute = React.memo(
  function WasserentwicklungRoute() {
    const navigate = useNavigate();
    const { t } = useTexts();

    const tabs = [
      {
        title: t("b.tab.1.title", "Die Bedeutung von Wasser"),
        path: "/wasserklaerer/wasserangebot/angebot",
        isActive: false,
      },
      {
        title: t("b.tab.2.title", "Wo kommt das Wasser her?"),
        path: "/wasserklaerer/wasserangebot/herkunft",
        isActive: false,
      },
      {
        title: t("b.tab.3.title", "Wasserverbrauch in Deutschland"),
        path: "/wasserklaerer/wasserangebot/verbrauch",
        isActive: false,
      },
      {
        title: t("b.tab.4.title", "Entwicklung des Wasserverbrauchs"),
        path: "/wasserklaerer/wasserangebot/entwicklung",
        isActive: true,
      },
    ];

    const chartOptions = {
      chart: {
        type: "column",
        height: 600,
      },
      title: {
        text: t(
          "wasserentwicklung.chart.title",
          "Wassergewinnung der öffentlichen Wasserversorgung, Bergbau und verarbeitendes Gewerbe, der Energieversorgung und der Landwirtschaft"
        ),
        style: {
          fontSize: "18px",
          fontWeight: "bold",
          color: "#2c3e50",
        },
      },
      xAxis: {
        categories: [
          "1991",
          "1995",
          "1998",
          "2001",
          "2004",
          "2007",
          "2010",
          "2013",
          "2016",
          "2019",
          "2022",
        ],
        title: {
          text: "Jahr",
        },
      },
      yAxis: {
        min: 0,
        max: 50,
        title: {
          text: "Wasserentnahme in Milliarden Kubikmeter",
        },
        stackLabels: {
          enabled: true,
          style: {
            fontWeight: "bold",
            color: "#666",
          },
        },
      },
      plotOptions: {
        column: {
          stacking: "normal",
          dataLabels: {
            enabled: false,
          },
        },
      },
      series: [
        {
          name: "Öffentliche Wasserversorgung",
          data: [4.6, 2.1, 2.0, 1.9, 1.8, 1.6, 1.6, 1.3, 1.2, 1.0, 0.9],
          color: "#1e88e5",
        },
        {
          name: "Bergbau und verarbeitendes Gewerbe",
          data: [4.6, 3.4, 3.2, 3.0, 2.8, 2.6, 2.6, 2.0, 1.9, 1.6, 1.4],
          color: "#26a69a",
        },
        {
          name: "Energieversorgung",
          data: [
            37.0, 37.0, 35.0, 33.0, 30.8, 27.8, 28.5, 21.7, 20.8, 17.3, 15.5,
          ],
          color: "#00897b",
        },
        {
          name: "Landwirtschaft",
          data: [0.1, 0.1, 0.2, 0.1, 0.1, 0.1, 0.1, 0.1, 0.1, 0.1, 0.1],
          color: "#66bb6a",
        },
      ],
      legend: {
        enabled: false,
      },
      tooltip: {
        headerFormat: "<b>{point.x}</b><br/>",
        pointFormat: "{series.name}: {point.y}<br/>Total: {point.stackTotal}",
      },
      credits: {
        enabled: false,
      },
    };

    return (
      <AppLayout>
        <ContentContainer>
          <h1>
            {t("wasserentwicklung.title", "Entwicklung des Wasserverbrauchs")}
          </h1>

          <TabContainer>
            {tabs.map((tab, index) => (
              <TabButton
                key={index}
                isActive={tab.isActive}
                onClick={() => !tab.isActive && navigate(tab.path)}
              >
                {tab.title}
              </TabButton>
            ))}
          </TabContainer>

          <ChartSection>
            <HighchartsReact highcharts={Highcharts} options={chartOptions} />
            <FigureCaption>
              {processInlineFormatting(
                t(
                  "wasserentwicklung.chart.caption",
                  "Abbildung 1: Entwicklung des Wasserverbrauchs nach Sektoren in Deutschland [1]"
                )
              )}
            </FigureCaption>
          </ChartSection>

          <Card>
            <h2>{t("wasserentwicklung.ueberblick.title", "Überblick")}</h2>

            <p>
              {processInlineFormatting(
                t(
                  "wasserentwicklung.ueberblick.p1",
                  "Die Abbildung zeigt einen eindeutigen Trend: Der Wasserverbrauch in Deutschland ist in den Bereichen öffentliche Wasserversorgung, Energieversorgung sowie Bergbau und verarbeitendes Gewerbe stetig rücklääufig. [1]"
                )
              )}
            </p>

            <p>
              {processInlineFormatting(
                t(
                  "wasserentwicklung.ueberblick.p2",
                  "Die Energieversorgung ist hierbei zwar der größte Wasserverbraucher, weist aber auch die stärkste Reduzierung im Laufe der Jahre auf \u2013 sie konnte ihren Wasserbedarf zwischen 1990 und 2022 um etwa 75 % senken. [1]"
                )
              )}
            </p>

            <p>
              {processInlineFormatting(
                t(
                  "wasserentwicklung.ueberblick.p3",
                  "Im Vergleich dazu fällt die Reduzierung des Wasserverbrauchs in der öffentlichen Wasserversorgung geringer aus. Im Jahr 2022 verbrauchte jeder Mensch in Deutschland im Durchschnitt 126 Liter Trinkwasser täglich. Dieser Wert liegt um 18 L beziehungsweise 12,5 % niedriger als im Jahr 1991. [2]"
                )
              )}
            </p>

            <p>
              {processInlineFormatting(
                t(
                  "wasserentwicklung.ueberblick.p4",
                  "Gründe dafür sind unter anderem gestiegene Wasserpreise, ein gestärktes Umweltbewusstsein, modernere Technik wie zum Beispiel wassersparende Armaturen und Haushaltsgeräte als auch die seit 20 Jahren besonders intensive Sanierung des Trinkwasserleitungsnetzes vor allem in der Region der ostdeutschen Bundesländer. [3]"
                )
              )}
            </p>
          </Card>

          <Card>
            <h2>
              {t(
                "wasserentwicklung.einsparpotenzial.title",
                "Begrenztes Einsparpotenzial im Privathaushalt"
              )}
            </h2>

            <p>
              {t(
                "wasserentwicklung.einsparpotenzial.p1",
                "Mittlerweile bietet dieser Sektor nur noch ein begrenztes Einsparpotenzial. Das liegt vor allem daran, dass die Weltgesundheitsorganisation einen täglichen Wasserbedarf von 100 L bis 200 L für einen optimalen hygienischen Standard empfiehlt."
              )}
            </p>

            <p>
              {t(
                "wasserentwicklung.einsparpotenzial.p2",
                "Ein kurzer Vergleich: In Notsituationen – etwa in der humanitären Hilfe – wird der tägliche Mindestbedarf an Wasser auf 15 bis 20 Liter pro Person geschätzt. Diese Menge deckt die nötigsten Bedürfnisse wie Trinken, Kochen und grundlegende Körperhygiene ab. Sie stellt zugleich das Minimum dar, um Infektionskrankheiten zu vermeiden."
              )}
            </p>

            <InfoBox>
              <h4>
                {t(
                  "wasserentwicklung.einsparpotenzial.infobox.title",
                  "Deutscher Wasserverbrauch im WHO-Vergleich"
                )}
              </h4>
              <p>
                {processInlineFormatting(
                  t(
                    "wasserentwicklung.einsparpotenzial.infobox.text",
                    "Mit einem durchschnittlichen täglichen Trinkwasserverbrauch von 126 L Tag pro Kopf in privaten Haushalten und im Kleingewerbe (2022) liegen wir Deutschen bereits im unteren Viertel der Empfehlung der WHO. [2]"
                  )
                )}
              </p>
            </InfoBox>

            <p>
              {processInlineFormatting(
                t(
                  "wasserentwicklung.einsparpotenzial.p3",
                  `Selbst wenn alle Menschen in Deutschland diesen Wert auf 100 L pro Kopf am Tag senken würden, würde dies einer Einsparung von etwa 800 Millionen m³ Wasser jährlich entsprechen. (Methodik Einsparung: 0,026 m3 * 365 Tage * 84,3 Millionen Einwohner = 800.007.000 m3) [4] Bezogen auf das gesamte in Deutschland zur Verfügung stehende Wasserdargebot von rund 176 Milliarden m³ (2022) entspräche die Einsparung prozentual einem Wert von etwa 0,45%. [5] Gleichzeitig bewirken diese Einsparungen, dass der Wasserverbrauch in Deutschland nur knapp über der von der WHO empfohlenen Mindestmenge zur Sicherstellung gesundheitlicher Standards liegt. Geschehen solche Einsparungen nicht durch modernere Technik und erhöhte Effizienz sondern durch willkürliches Sparen kann dies dementsprechend zu möglichem Komforteinbußen im Alltag führen. [3]`
                )
              )}
            </p>

            <p>
              {processInlineFormatting(
                t(
                  "wasserentwicklung.einsparpotenzial.p5",
                  "Ein weiterer Grund für das begrenzte Einsparpotenzial in der öffentlichen Wasserversorgung Deutschlands liegt an der bereits hohen Effizienz des deutschen Leitungsnetzes: Während in Deutschland im Jahr 2010 nur etwa 6,8 % des Trinkwassers durch Leckagen verloren gingen, liegt dieser Wert in anderen europäischen Ländern wie Frankreich oder Spanien bei 20\u201330 %. Deutschland ist damit das Land mit den niedrigsten Leitungsverlusten in Europa. [3]"
                )
              )}
            </p>
          </Card>

          <Card>
            <h2>
              {t(
                "wasserentwicklung.sparen.title",
                "Im privaten Haushalt: Wasser sparen oder nicht sparen?"
              )}
            </h2>

            <p>
              {t(
                "wasserentwicklung.sparen.p1",
                "Schließlich kann es sinnvoll sein sich beim Thema der Wassereinsparung auf andere Sektoren wie der Energieversorgung und der Industrie zu fokussieren, welche noch höhere Einsparpotenziale besitzen."
              )}
            </p>

            <p>
              {t(
                "wasserentwicklung.sparen.p2",
                "Am Ende bleibt es jedoch wichtig, die bisherigen Erfolge beim Rückgang des Wasserverbrauchs in privaten Haushalten zu sichern. Der gezielte Austausch hin zu effizienteren Sanitärgeräten sollte weiterhin vorangetrieben werden, um zusätzliche Einsparungen zu ermöglichen."
              )}
            </p>

            <InfoBox>
              <h4>
                {t(
                  "wasserentwicklung.sparen.infobox.title",
                  "Moderne Wassertechnologie"
                )}
              </h4>
              <p>
                {processInlineFormatting(
                  t(
                    "wasserentwicklung.sparen.infobox.text",
                    "Eine Untersuchung zu Entwicklungen in der Wassertechnologie aus dem Jahr 2002 kommt zu dem Ergebnis, dass bei einer umfassend modernen Ausstattung \u2013 darunter ein WC mit 5-Liter-Spülung und Spartaste, besonders wassersparende Armaturen und Duschköpfe sowie ein Urinal mit geringem Wasserverbrauch \u2013 ein täglicher Wasserverbrauch von 78 Litern pro Person in einem Vierpersonenhaushalt möglich ist. Dies wäre ohne Komfortverlust oder hygienische Risiken erreichbar. [3]"
                  )
                )}
              </p>
            </InfoBox>
          </Card>

          {/* Literaturverzeichnis */}
          <SourcesSection>
            <h3>
              {t("wasserentwicklung.literatur.title", "Literaturverzeichnis")}
            </h3>
            <ol>
              <li id="quelle-1">
                <LinkifyText>
                  {t(
                    "wasserentwicklung.literatur.ref1",
                    `[1] Umweltbundesamt (Hrsg.), \u201EWasserressourcen und ihre Nutzung\u201C, Umweltbundesamt, 2025. Zugegriffen: 1. Dezember 2025. [Online]. Verf\u00fcgbar unter: https://www.umweltbundesamt.de/daten/wasser/wasserressourcen-ihre-nutzung`
                  ).replace(/^\[\d+\]\s*/, "")}
                </LinkifyText>
              </li>
              <li id="quelle-2">
                <LinkifyText>
                  {t(
                    "wasserentwicklung.literatur.ref2",
                    `[2] Umweltbundesamt (Hrsg.), \u201EÖffentliche Wasserversorgung\u201C, Umweltbundesamt, 2024. Zugegriffen: 1. Dezember 2025. [Online]. Verf\u00fcgbar unter: https://www.umweltbundesamt.de/daten/wasser/wasserwirtschaft/oeffentliche-wasserversorgung`
                  ).replace(/^\[\d+\]\s*/, "")}
                </LinkifyText>
              </li>
              <li id="quelle-3">
                <LinkifyText>
                  {t(
                    "wasserentwicklung.literatur.ref3",
                    `[3] C. Baumgarten, J. Rechenberg, S. Richter, I. Chorus, L. Vigelahn, und O. Schmoll, \u201EWassersparen in Privathaushalten: sinnvoll, ausgereizt, \u00fcbertrieben? - Fakten, Hintergr\u00fcnde, Empfehlungen\u201C, Umweltbundesamt, 2014. [Online]. Verf\u00fcgbar unter: https://www.umweltbundesamt.de/sites/default/files/medien/479/publikationen/hgp_wassersparen_in_privathaushalten_web.pdf`
                  ).replace(/^\[\d+\]\s*/, "")}
                </LinkifyText>
              </li>
              <li id="quelle-4">
                <LinkifyText>
                  {t(
                    "wasserentwicklung.literatur.ref4",
                    `[4] Statistisches Bundesamt (Hrsg.), \u201EBev\u00f6lkerung im Jahr 2022 auf 84,3 Millionen gewachsen\u201C, Destatis, 2025. Zugegriffen: 1. Dezember 2025. [Online]. Verf\u00fcgbar unter: https://www.destatis.de/DE/Presse/Pressemitteilungen/2023/01/PD23_026_124.html`
                  ).replace(/^\[\d+\]\s*/, "")}
                </LinkifyText>
              </li>
              <li id="quelle-5">
                <LinkifyText>
                  {t(
                    "wasserentwicklung.literatur.ref5",
                    `[5] Umweltbundesamt (Hrsg.), \u201EWasserdargebot und Wassernutzung 2022\u201C, Umweltbundesamt, 2022. Zugegriffen: 1. Dezember 2025. [Online]. Verf\u00fcgbar unter: https://www.umweltbundesamt.de/bild/wasserdargebot-wassernutzung-2022`
                  ).replace(/^\[\d+\]\s*/, "")}
                </LinkifyText>
              </li>
            </ol>
          </SourcesSection>
        </ContentContainer>
      </AppLayout>
    );
  }
);
